import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { enhancedSearch } from '../utils/searchUtils';
import { Briefcase, Plus, Search, Edit, Trash2, Eye, Users, FileText, X, ArrowUpDown, GripVertical, Filter } from 'lucide-react';
import { LocalStorageManager } from '../utils/localStorage';
import { addToRecycleBin } from '../utils/recycleBin';
import { useNotifications } from '../contexts/NotificationContext';
import { Job } from '../types';
import { StatusBadge, jobStatusOptions } from '../components/ui/StatusBadge';
import { subscribeToJobs, addJob, updateJob, deleteJob } from '../services/jobService';

type ColumnConfig = {
  [key: string]: {
    title: string;
    width: string;
    sortable: boolean;
    sortField?: keyof Job;
  };
};

const columnConfig: ColumnConfig = {
  select: { title: '', width: 'w-12', sortable: false },
  positions: { title: 'Positions', width: '', sortable: true, sortField: 'title' },
  gender: { title: 'Gender', width: '', sortable: false },
  salary: { title: 'Salary', width: '', sortable: true, sortField: 'salaryRange' },
  city: { title: 'City', width: '', sortable: true, sortField: 'city' },
  country: { title: 'Country', width: '', sortable: true, sortField: 'country' },
  company: { title: 'Company Name', width: '', sortable: true, sortField: 'company' },
  experience: { title: 'Experience', width: '', sortable: true, sortField: 'experience' },
  accommodation: { title: 'Accommodation', width: '', sortable: true, sortField: 'accommodation' },
  ageMaximumRange: { title: 'Age Range', width: '', sortable: true, sortField: 'ageMaximumRange' },
  nationality: { title: 'Nationality', width: '', sortable: true, sortField: 'nationality' },
  dutyHours: { title: 'Duty Hours', width: '', sortable: true, sortField: 'dutyHours' },
  workDays: { title: 'Work Days', width: '', sortable: true, sortField: 'workDays' },
  overtime: { title: 'Overtime', width: '', sortable: true, sortField: 'overtime' },
  transportProvision: { title: 'Transport', width: '', sortable: true, sortField: 'transportProvision' },
  contractPeriod: { title: 'Contract Period', width: '', sortable: true, sortField: 'contractPeriod' },
  vacancies: { title: 'Vacancies Left', width: '', sortable: true, sortField: 'vacancies' },
  linkedCandidates: { title: 'Linked Candidates', width: '', sortable: false },
  uploads: { title: 'Uploads', width: '', sortable: false },
  status: { title: 'Status', width: '', sortable: true, sortField: 'status' },
  additionalDetails: { title: 'Additional Details', width: '', sortable: false },
  actions: { title: 'Actions', width: '', sortable: false },
};

const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const { notifyBadgeChange, notifyContentChange } = useNotifications();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showLinkedCandidatesModal, setShowLinkedCandidatesModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  
  // Enhanced state for filtering and column management
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState([
    'select', 'positions', 'gender', 'salary', 'city', 'country', 'company', 
    'experience', 'accommodation', 'ageMaximumRange', 'nationality', 'dutyHours', 
    'workDays', 'overtime', 'transportProvision', 'contractPeriod', 'vacancies', 
    'linkedCandidates', 'uploads', 'status', 'additionalDetails', 'actions'
  ]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to Firebase jobs
  useEffect(() => {
    console.log('💼 Jobs: Setting up Firebase subscription...');
    setLoading(true);
    
    // Clear any old localStorage jobs to prevent duplicates
    const localStorageKey = 'recruitment_crm_jobs';
    const localJobs = localStorage.getItem(localStorageKey);
    if (localJobs) {
      console.log('🧹 Clearing old localStorage jobs to prevent duplicates');
      localStorage.removeItem(localStorageKey);
    }
    
    const unsubscribe = subscribeToJobs((firebaseJobs) => {
      console.log('💼 Jobs: Received', firebaseJobs.length, 'jobs from Firebase');
      setJobs(firebaseJobs);
      setLoading(false);
    });

    return () => {
      console.log('💼 Jobs: Cleaning up Firebase subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Listen for status changes from other pages
  useEffect(() => {
    const handleJobStatusUpdate = (event: CustomEvent) => {
      const { id, newStatus } = event.detail;
      setJobs(prev => prev.map(j => 
        j.id === id ? { ...j, status: newStatus } : j
      ));
    };

    // Add event listeners
    window.addEventListener('jobStatusChanged', handleJobStatusUpdate as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('jobStatusChanged', handleJobStatusUpdate as EventListener);
    };
  }, []);

  // Helper function to get job ID
  const getJobId = (job: Job): string => {
    return job.id;
  };

  // Helper function to get linked candidates count
  const getLinkedCandidatesCount = (jobId: string): number => {
    const jobCandidatesKey = `job_candidates_${jobId}`;
    const linkedCandidates = JSON.parse(localStorage.getItem(jobCandidatesKey) || '[]');
    return linkedCandidates.length;
  };

  // Helper function to get linked candidates
  const getLinkedCandidates = (jobId: string): string[] => {
    const jobCandidatesKey = `job_candidates_${jobId}`;
    return JSON.parse(localStorage.getItem(jobCandidatesKey) || '[]');
  };

  // Helper functions to format job data
  const formatSalary = (job: Job): string => {
    const { min, max, currency } = job.salaryRange;
    if (min === 0 && max === 0) return 'Negotiable';
    if (min === max) return `${min} ${currency}`;
    return `${min} - ${max} ${currency}`;
  };

  const formatPositions = (job: Job): string => {
    return job.positions?.join(', ') || job.title;
  };

  const formatGender = (job: Job): string => {
    return job.gender?.join(', ') || 'Any';
  };

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Selection handlers
  const handleSelectJob = (jobId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedJobs(prev => [...prev, jobId]);
    } else {
      setSelectedJobs(prev => prev.filter(id => id !== jobId));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedJobs(filteredAndSortedJobs.map(job => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedJobs.length} job(s)? They will be moved to the recycle bin.`)) {
      try {
        const jobsToDelete = jobs.filter(j => selectedJobs.includes(j.id));
        console.log('🗑️ Bulk deleting jobs:', jobsToDelete.map(j => ({ id: j.id, title: j.title })));
        console.log('💼 Jobs before bulk deletion:', jobs.length);
        
        let deletedCount = 0;
        
        for (const id of selectedJobs) {
          const job = jobs.find(j => j.id === id);
          if (job) {
            try {
              // Delete from Firebase (which is the source of truth)
              await deleteJob(id);
              console.log(`🔥 Deleted job ${id} from Firebase`);
              
              // Also delete linked candidates data
              const jobCandidatesKey = `job_candidates_${id}`;
              localStorage.removeItem(jobCandidatesKey);
              
              // Add to recycle bin
              addToRecycleBin({
                type: 'job',
                name: job.title,
                details: `${job.company} | ${job.location} | Status: ${job.status}`,
                deletedBy: 'Current User',
                data: job
              });
              
              deletedCount++;
              console.log(`✅ Successfully deleted job "${job.title}" and cleaned up linked candidates`);
            } catch (error) {
              console.error(`Failed to delete job "${job.title}":`, error);
            }
          }
        }
        
        // Clear selection
        setSelectedJobs([]);
        
        // Notify content changes
        jobsToDelete.forEach(job => {
          notifyContentChange('Jobs Page', 'deleted', 'Job', job.title);
        });
        
        if (deletedCount === selectedJobs.length) {
          alert(`✅ ${deletedCount} job(s) successfully moved to recycle bin!`);
        } else {
          alert(`⚠️ ${deletedCount}/${selectedJobs.length} job(s) moved to recycle bin. Some jobs may not have been deleted properly.`);
        }
        
      } catch (error) {
        console.error('Failed to delete jobs:', error);
        alert('❌ Failed to delete jobs. Please try again.');
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  // Get unique values for filters
  const getUniqueCompanies = () => {
    const companies = jobs
      .map(job => job.company)
      .filter(Boolean)
      .filter((company, index, array) => array.indexOf(company) === index);
    return companies;
  };

  const getUniqueCountries = () => {
    const countries = jobs
      .map(job => job.country)
      .filter(Boolean)
      .filter((country, index, array) => array.indexOf(country) === index);
    return countries;
  };

  const handleStatusChange = async (jobId: string, newStatus: string) => {
    try {
      const job = jobs.find(j => j.id === jobId);
      if (!job) return;
      
      const oldStatus = job.status;
      
      // Update in Firebase
      await updateJob(jobId, { status: newStatus });
      console.log(`🔥 Updated job status in Firebase: ${oldStatus} → ${newStatus}`);

      // Notify badge change for cross-page sync
      notifyBadgeChange('Jobs Page', `${job.title} Status`, oldStatus, newStatus);
      
      // Dispatch event for cross-page sync
      window.dispatchEvent(new CustomEvent('jobStatusChanged', { 
        detail: { id: jobId, oldStatus, newStatus, jobTitle: job.title }
      }));

      console.log(`📊 Updated job ${job.title} status: ${oldStatus} → ${newStatus}`);
    } catch (error) {
      console.error('Failed to update job status:', error);
      alert('Failed to update job status. Please try again.');
    }
  };

  const handleEdit = async (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      const newTitle = prompt('Edit Job Title:', job.title);
      if (newTitle && newTitle !== job.title) {
        try {
          // Update in Firebase
          await updateJob(jobId, { title: newTitle });
          console.log(`🔥 Updated job title in Firebase: ${job.title} → ${newTitle}`);
          
          // Notify content change
          notifyContentChange('Jobs Page', 'updated', 'Job', `${job.title} → ${newTitle}`);
        } catch (error) {
          console.error('Failed to update job title:', error);
          alert('Failed to update job title. Please try again.');
        }
      }
    }
  };

  const handleDelete = async (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job && window.confirm(`Are you sure you want to delete the job "${job.title}"? It will be moved to the recycle bin.`)) {
      try {
        console.log('🗑️ Attempting to delete job:', job);
        console.log('💼 Jobs before deletion:', jobs.length);
        
        // Delete from Firebase (which is the source of truth)
        await deleteJob(jobId);
        console.log('🔥 Deleted job from Firebase:', jobId);
        
        // Also delete linked candidates data
        const jobCandidatesKey = `job_candidates_${jobId}`;
        localStorage.removeItem(jobCandidatesKey);
        
        // Add to recycle bin
        addToRecycleBin({
          type: 'job',
          name: job.title,
          details: `${job.company} | ${job.location} | Status: ${job.status}`,
          deletedBy: 'Current User',
          data: job
        });
        
        // Notify content change
        notifyContentChange('Jobs Page', 'deleted', 'Job', job.title);
        
        alert('✅ Job successfully moved to recycle bin!');
      } catch (error) {
        console.error('Failed to delete job:', error);
        alert('❌ Failed to delete job. Please try again.');
      }
    }
  };

  const handleView = (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      const details = [
        `Title: ${job.title}`,
        `Company: ${job.company}`,
        `Location: ${job.location}`,
        `Salary: ${formatSalary(job)}`,
        `Positions: ${formatPositions(job)}`,
        `Gender: ${formatGender(job)}`,
        `Experience: ${job.experience || 'Not specified'}`,
        `Accommodation: ${job.accommodation || 'Not specified'}`,
        `Vacancies: ${job.vacancies || 1}`,
        `Requirements: ${job.requirements?.join(', ') || 'None specified'}`,
        `Benefits: ${job.benefits?.join(', ') || 'None specified'}`,
        `Additional Details: ${job.additionalDetails || 'None'}`
      ];
      alert(`Job Details:\n\n${details.join('\n')}`);
    }
  };

  const filteredAndSortedJobs = useMemo(() => {
    let filtered = jobs;

    // Apply enhanced search filter for better partial number matching
    if (searchTerm) {
      filtered = filtered.filter(job => {
        const searchableFields = [
          job.title,
          job.company,
          job.location,
          job.status,
          job.description,
          (job.requirements || []).join(' '),
          (job.benefits || []).join(' '),
          // Extended fields from Job Generator
          job.positions?.join(', ') || '',
          job.gender?.join(', ') || '',
          job.city || '',
          job.country || '',
          job.experience || '',
          job.accommodation || '',
          job.additionalDetails || '',
          `${job.salaryRange?.min || ''} ${job.salaryRange?.max || ''} ${job.salaryRange?.currency || ''}`,
          job.id || ''
        ];
        
        return enhancedSearch(searchTerm, searchableFields);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    // Apply company filter
    if (companyFilter !== 'all') {
      filtered = filtered.filter(job => job.company === companyFilter);
    }

    // Apply country filter
    if (countryFilter !== 'all') {
      filtered = filtered.filter(job => job.country === countryFilter);
    }

    // Apply sorting
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        // Handle special cases for city and country sorting
        if (sortField === 'city') {
          aValue = (a.city || 'N/A').toLowerCase();
          bValue = (b.city || 'N/A').toLowerCase();
        } else if (sortField === 'country') {
          aValue = (a.country || 'N/A').toLowerCase();
          bValue = (b.country || 'N/A').toLowerCase();
        } else {
          aValue = a[sortField as keyof typeof a];
          bValue = b[sortField as keyof typeof b];
          
          if (typeof aValue === 'string') aValue = aValue.toLowerCase();
          if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    return filtered;
  }, [jobs, searchTerm, statusFilter, companyFilter, countryFilter, sortField, sortDirection]);

  const renderJobCell = (job: Job, columnId: string) => {
    switch (columnId) {
      case 'select':
        return (
          <input
            type="checkbox"
            checked={selectedJobs.includes(job.id)}
            onChange={(e) => handleSelectJob(job.id, e.target.checked)}
            className="rounded"
          />
        );
      case 'positions':
        return (
          <div className="font-medium text-[#8b6914]">{formatPositions(job)}</div>
        );
      case 'gender':
        return (
          <div className="text-gray-600">{formatGender(job)}</div>
        );
      case 'salary':
        return (
          <div className="text-gray-600">{formatSalary(job)}</div>
        );
      case 'city':
        return (
          <div className="text-gray-600">{job.city || 'N/A'}</div>
        );
      case 'country':
        return (
          <div className="text-gray-600">{job.country || 'N/A'}</div>
        );
      case 'company':
        return (
          <div className="text-gray-600">{job.company}</div>
        );
      case 'experience':
        return (
          <div className="text-gray-600">{job.experience || 'Not specified'}</div>
        );
      case 'accommodation':
        return (
          <div className="text-gray-600">{job.accommodation || 'Not specified'}</div>
        );
      case 'ageMaximumRange':
        return (
          <div className="text-gray-600">{job.ageMaximumRange || 'Not specified'}</div>
        );
      case 'nationality':
        return (
          <div className="text-gray-600">{job.nationality || 'Any'}</div>
        );
      case 'dutyHours':
        return (
          <div className="text-gray-600">{job.dutyHours || 'Not specified'}</div>
        );
      case 'workDays':
        return (
          <div className="text-gray-600">{job.workDays || 'Not specified'}</div>
        );
      case 'overtime':
        return (
          <div className="text-gray-600">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              job.overtime === 'Available' ? 'bg-green-100 text-green-800' : 
              job.overtime === 'Not Available' ? 'bg-red-100 text-red-800' : 
              'bg-gray-100 text-gray-800'
            }`}>
              {job.overtime || 'Not specified'}
            </span>
          </div>
        );
      case 'transportProvision':
        return (
          <div className="text-gray-600">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              job.transportProvision === 'Provided' ? 'bg-green-100 text-green-800' : 
              job.transportProvision === 'Not Provided' ? 'bg-red-100 text-red-800' : 
              'bg-gray-100 text-gray-800'
            }`}>
              {job.transportProvision || 'Not specified'}
            </span>
          </div>
        );
      case 'contractPeriod':
        return (
          <div className="text-gray-600">{job.contractPeriod || 'Not specified'}</div>
        );
      case 'vacancies':
        const linkedCandidatesCount = getLinkedCandidatesCount(job.id);
        const vacanciesLeft = (job.vacancies || 1) - linkedCandidatesCount;
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            vacanciesLeft > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {vacanciesLeft} left
          </span>
        );
      case 'linkedCandidates':
        const candidatesCount = getLinkedCandidatesCount(job.id);
        return (
          <button
            onClick={() => {
              setSelectedJobId(job.id);
              setShowLinkedCandidatesModal(true);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
          >
            <Users className="w-3 h-3 mr-1" />
            {candidatesCount} linked
          </button>
        );
      case 'uploads':
        return (
          <div>
            {job.uploads && job.uploads.length > 0 ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <FileText className="w-3 h-3 mr-1" />
                {job.uploads.length} file{job.uploads.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-gray-400">None</span>
            )}
          </div>
        );
      case 'status':
        return (
          <StatusBadge
            currentStatus={job.status.toLowerCase() || 'draft'}
            options={jobStatusOptions}
            onStatusChange={(newStatus) => handleStatusChange(job.id, newStatus)}
            size="sm"
          />
        );
      case 'additionalDetails':
        return (
          <div className="max-w-xs truncate text-gray-600" title={job.additionalDetails || ''}>
            {job.additionalDetails || 'None'}
          </div>
        );
      case 'actions':
        return (
          <div className="flex space-x-2">
            <button 
              onClick={() => handleView(job.id)}
              className={recruitmentTheme.components.button.ghost}
              title="View details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleEdit(job.id)}
              className={recruitmentTheme.components.button.ghost}
              title="Edit job"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleDelete(job.id)}
              className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700`}
              title="Delete job"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>
            Job Openings
          </h1>
          <p className="text-gray-600 mt-1">
            Manage available job positions and track applications
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={recruitmentTheme.components.card}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Briefcase className="w-5 h-5 text-[#8b6914]" />
            <h2 className={themeClasses.sectionTitle}>Available Positions</h2>
            <span className="text-sm text-gray-500">({filteredAndSortedJobs.length})</span>
            <button 
              onClick={() => navigate('/job-generator')}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 ml-4`}
            >
              <Plus className="w-4 h-4" />
              <span>Add Job</span>
            </button>
            <div className="relative ml-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search jobs, companies, locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${recruitmentTheme.components.input} pl-10 w-80`}
                data-search="local"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Closed">Closed</option>
              <option value="Draft">Draft</option>
            </select>
            
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Companies</option>
              {getUniqueCompanies().map(company => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
            
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className={recruitmentTheme.components.select}
            >
              <option value="all">All Countries</option>
              {getUniqueCountries().map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedJobs.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm text-blue-700">
              {selectedJobs.length} job(s) selected
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDeleteSelected}
                className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-1`}
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete Selected</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={selectedJobs.length === filteredAndSortedJobs.length && filteredAndSortedJobs.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="rounded"
            />
            <span>Select All</span>
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {columnOrder.map((columnId) => {
                  const config = columnConfig[columnId as keyof typeof columnConfig];
                  return (
                    <th 
                      key={columnId}
                      className={`text-left py-3 px-4 font-medium text-[#8b6914] ${config.width} ${
                        columnId !== 'select' && columnId !== 'actions' ? 'cursor-move' : ''
                      }`}
                      draggable={columnId !== 'select' && columnId !== 'actions'}
                      onDragStart={(e) => handleDragStart(e, columnId)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, columnId)}
                      style={{
                        backgroundColor: draggedColumn === columnId ? '#f3f4f6' : undefined
                      }}
                    >
                      <div className="flex items-center space-x-1">
                        {columnId !== 'select' && columnId !== 'actions' && (
                          <GripVertical className="w-3 h-3 text-gray-400" />
                        )}
                        {columnId === 'select' ? (
                          <input
                            type="checkbox"
                            checked={selectedJobs.length === filteredAndSortedJobs.length && filteredAndSortedJobs.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="rounded"
                          />
                        ) : config.sortable ? (
                          <button 
                            onClick={() => handleSort(config.sortField!)}
                            className="flex items-center space-x-1 hover:text-[#b8860b] transition-colors"
                          >
                            <span>{config.title}</span>
                            <ArrowUpDown className="w-3 h-3" />
                          </button>
                        ) : (
                          <span>{config.title}</span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedJobs.map((job, index) => (
                <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50">
                  {columnOrder.map((columnId) => (
                    <td key={columnId} className="py-3 px-4">
                      {columnId === 'positions' ? (
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                            {index + 1}.
                          </span>
                          <div className="font-medium text-[#8b6914]">{formatPositions(job)}</div>
                        </div>
                      ) : (
                        renderJobCell(job, columnId)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Linked Candidates Modal */}
      {showLinkedCandidatesModal && selectedJobId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#8b6914]">
                Linked Candidates for {jobs.find(j => j.id === selectedJobId)?.title || 'Job'}
              </h2>
              <button 
                onClick={() => setShowLinkedCandidatesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available Candidates */}
              <div>
                <h3 className="text-lg font-semibold text-[#8b6914] mb-4">Available Candidates</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {LocalStorageManager.getCandidates()
                    .filter(candidate => !getLinkedCandidates(selectedJobId).includes(candidate.id))
                    .map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{candidate.name}</p>
                          <p className="text-sm text-gray-600">{candidate.jobTitle || 'No job title'}</p>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                        <button
                          onClick={() => {
                            const currentLinked = getLinkedCandidates(selectedJobId);
                            const updatedLinked = [...currentLinked, candidate.id];
                            const jobCandidatesKey = `job_candidates_${selectedJobId}`;
                            localStorage.setItem(jobCandidatesKey, JSON.stringify(updatedLinked));
                            // Force re-render
                            setJobs([...jobs]);
                          }}
                          className="flex items-center space-x-1 px-3 py-1 bg-[#8b6914] text-white rounded text-sm hover:bg-[#b8860b] transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Link</span>
                        </button>
                      </div>
                    ))}
                </div>
              </div>
              
              {/* Linked Candidates */}
              <div>
                <h3 className="text-lg font-semibold text-[#8b6914] mb-4">
                  Linked Candidates ({getLinkedCandidatesCount(selectedJobId)})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {getLinkedCandidates(selectedJobId).map(candidateId => {
                    const candidate = LocalStorageManager.getCandidateById(candidateId);
                    if (!candidate) return null;
                    
                    return (
                      <div key={candidate.id} className="flex items-center justify-between p-3 border border-[#8b6914] bg-[#8b6914]/5 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{candidate.name}</p>
                          <p className="text-sm text-gray-600">{candidate.jobTitle || 'No job title'}</p>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate('/candidates', { state: { editCandidate: candidate } })}
                            className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                            title="Edit candidate details"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => {
                              const currentLinked = getLinkedCandidates(selectedJobId);
                              const updatedLinked = currentLinked.filter(id => id !== candidate.id);
                              const jobCandidatesKey = `job_candidates_${selectedJobId}`;
                              localStorage.setItem(jobCandidatesKey, JSON.stringify(updatedLinked));
                              // Force re-render
                              setJobs([...jobs]);
                            }}
                            className="flex items-center space-x-1 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                          >
                            <X className="w-4 h-4" />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {getLinkedCandidatesCount(selectedJobId) === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p>No candidates linked to this job yet.</p>
                      <p className="text-sm">Select candidates from the left to link them.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => setShowLinkedCandidatesModal(false)}
                className={recruitmentTheme.components.button.secondary}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsPage;